// src/pages/Batches/subpages/BatchEdit.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';

import useBatchService from '../../../app/services/hooks/useBatchService';
import { BatchDataForm } from '../../../app/models/Batch';
import BatchFormContainer from '../components/BatchFormContainer';
import BatchHistoryTab from '../components/tables/BatchHistoryTab';
import BatchSummaryTab from '../components/tables/BatchSummaryTab';

type RouterParams = { id: string };
type SelectOpt = { id: number | string; name: string };

const BatchEdit: React.FC = () => {
  const { id } = useParams<RouterParams>();
  const batchId = Number(id);

  const { showLoading, hideLoading } = useContext(AppContext);
  const { editBatch, updateBatch, fetchingEditBatch, fetchingUpdateBatch } = useBatchService();

  const editRef = useRef(editBatch);
  const updateRef = useRef(updateBatch);
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  useEffect(() => { editRef.current = editBatch; }, [editBatch]);
  useEffect(() => { updateRef.current = updateBatch; }, [updateBatch]);
  useEffect(() => { showLoadingRef.current = showLoading; }, [showLoading]);
  useEffect(() => { hideLoadingRef.current = hideLoading; }, [hideLoading]);

  const [verifying, setVerifying] = useState<boolean>(true);
  const [reloadTick, setReloadTick] = useState(0);

  const [batch, setBatch] = useState<BatchDataForm>({
    name: '',
    batch_category: 'COLLECTION',
    quantity: 0,
    is_active: true,
  });

  const [projects, setProjects] = useState<SelectOpt[]>([]);
  const [centers, setCenters] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [batchCategories, setBatchCategories] = useState<SelectOpt[]>([]);
  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [warehouses, setWarehouses] = useState<SelectOpt[]>([]);
  const [products, setProducts] = useState<SelectOpt[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  const load = useCallback((bid: number) => {
    showLoadingRef.current?.('loading', 'Cargando lote...');
    editRef.current(bid, {
      onSuccess: (resp: ServiceResponse) => {
        setBatch(resp.data.batch as BatchDataForm);
        setProjects(resp.data.projects || []);
        setCenters(resp.data.centers || []);
        setUnits(resp.data.units || []);
        setBatchCategories(resp.data.batch_categories || []);
        setSocieties(resp.data.societies || []);
        setWarehouses(resp.data.warehouses || []);
        setProducts(resp.data.products || []);
        setHistory(resp.data.history || []);
        hideLoadingRef.current?.();
        setVerifying(false);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message, { autoClose: 2500 });
        setVerifying(false);
      }
    });
  }, []);

  useEffect(() => { if (Number.isFinite(batchId)) load(batchId); }, [batchId, load]);

  const onSave = () => {
    showLoadingRef.current?.('loading', 'Actualizando lote...');
    // Normalizar harvest_date: convertir string vacío a undefined
    const batchToUpdate = { ...batch };
    if (batchToUpdate.harvest_date === '' || batchToUpdate.harvest_date === null || batchToUpdate.harvest_date === undefined) {
      batchToUpdate.harvest_date = undefined;
    }
    updateRef.current(batchId, batchToUpdate, {
      onSuccess: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.success(resp.message);
        setErrorFields(undefined);
        
        load(batchId);
        setReloadTick(prev => prev + 1);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoadingRef.current?.();
        setErrorFields(errs.data);
      }
    });
  };

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Lotes', url: '/batches', isActive: false },
    { name: `Editar Lote #${batchId}`, url: `/batches/${batchId}/edit`, isActive: true },
  ];

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try { const date = new Date(d); return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString(); }
    catch { return d as string; }
  };

  const createdByName =
    (batch as any).created_by_name ||
    (batch as any).created_by_fullname ||
    [ (batch as any)?.created_by?.first_name, (batch as any)?.created_by?.last_name ].filter(Boolean).join(' ') ||
    (batch as any)?.created_by?.username || '';

  const updatedByName =
    (batch as any).updated_by_name ||
    (batch as any).updated_by_fullname ||
    [ (batch as any)?.updated_by?.first_name, (batch as any)?.updated_by?.last_name ].filter(Boolean).join(' ') ||
    (batch as any)?.updated_by?.username || '';

  const availabilityStatusLabel =
    (batch as any).availability_status_label ??
    (batch.availability_status === 'COLLECTED'
      ? 'Acopiado'
      : batch.availability_status === 'FREE'
        ? 'Libre disponibilidad'
        : (batch.availability_status as string) || '');

  const quantityNum = Number(batch.quantity ?? batch.quantity_in_use ?? 0);
  const isLockedByQuantity = !Number.isNaN(quantityNum) && quantityNum > 0;

  if (verifying) {
    return (
      <>
        <Breadcrumbs pageSection={`Editar Lote #${batchId}`} breadcrumbs={breadcrumbs} />
        <DefaultCard><LazyLoading height={300} /></DefaultCard>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs pageSection={`Editar Lote #${batchId}`} breadcrumbs={breadcrumbs} />

      {/*  NUEVO: Tabs superiores (igual que SelectionOrderEdit) */}
      <Tabs defaultActiveKey="general" id="batch-edit-tabs" className="mb-3">
        {/* === TAB 1: Datos Generales === */}
        <Tab eventKey="general" title="Datos Generales">
          {/* Fila con formulario y metadata */}
          <div className="row">
            {/* Izquierda: Formulario editable */}
            <div className="col-12 col-lg-8">
              <DefaultCard>
                <h5 className="mb-3">Datos del lote</h5>
                {isLockedByQuantity && (
                  <div className="alert alert-info mb-3 py-2" role="alert">
                    Los datos del lote están bloqueados porque la cantidad en uso es mayor a 0. Para modificar estos campos, la cantidad debe ser 0.
                  </div>
                )}
                <BatchFormContainer
                  fetching={!!(fetchingEditBatch || fetchingUpdateBatch)}
                  action={onSave}
                  cancel={() => window.history.back()}
                  batch={batch}
                  setBatch={setBatch}
                  projects={projects}
                  setProjects={setProjects}
                  centers={centers}
                  setCenters={setCenters}
                  units={units}
                  setUnits={setUnits}
                  batchCategories={batchCategories}
                  setBatchCategories={setBatchCategories}
                  societies={societies}
                  setSocieties={setSocieties}
                  warehouses={warehouses}
                  setWarehouses={setWarehouses}
                  products={products}
                  setProducts={setProducts}
                  errorFields={errorFields}
                  disabled={isLockedByQuantity}
                />
              </DefaultCard>
            </div>

            {/* Derecha: Metadata de auditoría */}
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Datos del creador</h5>
                <div className="mb-2">
                  <label className="form-label">Creado por</label>
                  <input className="form-control" value={createdByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de creación</label>
                  <input className="form-control" value={formatDate((batch as any).created_at)} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Editado por</label>
                  <input className="form-control" value={updatedByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de actualización</label>
                  <input className="form-control" value={formatDate((batch as any).updated_at)} disabled />
                </div>
                <hr />
                <div className="mb-2">
                  <label className="form-label">Activo</label>
                  <input className="form-control" value={batch.is_active ? 'Sí' : 'No'} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Estado del lote</label>
                  <input
                    className="form-control"
                    value={availabilityStatusLabel}
                    disabled
                  />
                </div>
              </DefaultCard>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* Sección de información adicional debajo de Datos Generales */}
      <div className="mt-4">
        <Tabs defaultActiveKey="summary" id="batch-info-tabs" className="mb-3">
          {/* === TAB 1: Resumen === */}
          <Tab eventKey="summary" title="Resumen">
            <BatchSummaryTab batch={batch} />
          </Tab>

          {/* === TAB 2: Historial === */}
          <Tab eventKey="history" title="Historial">
            <BatchHistoryTab history={history} />
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default BatchEdit;